/**
 * Os blocos da tela "Contratos digitais" (E177.F4, GUS-253, ADR-0130; HTML 6.8 por CCB). Regra
 * pura, sem JSX.
 *
 * - **Um bloco por CCB declarada**, na ordem do servidor, com as unidades de origem e as vinculadas
 *   de outra CCB; depois, os blocos ainda sem número (só na tela).
 * - **Rótulo**: "CCB 4201", ou "CCB 2 (sem número)" no bloco ainda sem número.
 * - **Compartilhado**: a unidade de origem que outra CCB também usa diz com quais; a vinculada diz em
 *   qual CCB foi enviada (o arquivo sem CCB vinculado não tem origem).
 * - **Do recibo**: o envio do bloco sem conclusão (o recibo não fechado ao recarregar a página) e o
 *   guardado sem exame vão no bloco da CCB do envio; os do envio sem CCB ficam na tela.
 * - **Sem CCB**: o arquivo que não entrou num bloco (enviado antes) vira pergunta no servidor; a tela
 *   o lista à parte, para vincular ou remover.
 * - **Conferir** só com todas as CCBs numeradas, cada uma com arquivo de origem recebido, e nada em
 *   envio, com falha ou com o mesmo número de outro bloco.
 * - **Número em edição** (E177.F4.C01, F-E177.F4-01): o número apagado, repetido, que falhou ao gravar,
 *   em gravação ou digitado e ainda não gravado segura o "Conferir" — o servidor só conhece o gravado.
 * - **Homônimo** (E177.F4.C02, F-E177.F4-02): o arquivo com o mesmo nome de outro documento da tela
 *   leva o identificador curto do documento.
 * - **Cópia com outro nome** (E177.F3.C03, GUS-253 itens 5 e 6): a cópia idêntica que este bloco
 *   recebeu aparece com o nome com que chegou aqui, e o selo diz o nome e a CCB da origem.
 */
import { ptBR } from '@/i18n/pt-BR';
import type { NaoExaminadoNaTela } from '@/components/CaseWizard/LinhasDoEnvio';
import {
  recebimentosAbertos,
  type CcbDoContratoData,
  type EnvioDaAberturaData,
  type ReciboDaCustodiaData,
  type UnidadeDoEnvioData,
} from '@/lib/abertura_do_caso';

const t = ptBR.abertura.contratosDigitais;

export interface VinculadaDaCcb {
  /** Com o nome com que este bloco recebeu a cópia idêntica, quando recebeu (E177.F3.C03). */
  unidade: UnidadeDoEnvioData;
  /** O bloco em que o arquivo foi enviado; nulo no arquivo sem CCB que a pessoa vinculou. */
  origem: CcbDoContratoData | null;
  /** E177.F3.C03: o nome do arquivo na origem, quando este bloco o recebeu com outro nome. */
  nomeNaOrigem: string | null;
}

/** A vinculada com o nome do recebimento deste bloco; o da origem fica à parte, se diferente. */
function vinculada(unidade: UnidadeDoEnvioData, origem: CcbDoContratoData | null): VinculadaDaCcb {
  const recebida = unidade.recebida_como ?? null;
  if (recebida === null || recebida === unidade.nome)
    return { unidade, origem, nomeNaOrigem: null };
  return { unidade: { ...unidade, nome: recebida }, origem, nomeNaOrigem: unidade.nome };
}

export interface BlocoDaCcb {
  /** O id da CCB, ou a chave do bloco ainda sem número. */
  chave: string;
  ccb: CcbDoContratoData | null;
  unidades: UnidadeDoEnvioData[];
  vinculadas: VinculadaDaCcb[];
}

/** Os blocos das CCBs declaradas, na ordem dos blocos, e os sem número no fim. */
export function blocosDoEnvio(
  envio: EnvioDaAberturaData | null | undefined,
  semNumero: readonly string[],
): BlocoDaCcb[] {
  const declaradas = (envio?.contratos ?? []).flatMap((c) =>
    c.ccb != null ? [{ contrato: c, ccb: c.ccb }] : [],
  );
  const origem = new Map<string, CcbDoContratoData>();
  for (const { contrato, ccb } of declaradas) {
    for (const u of contrato.unidades) origem.set(u.documento_id, ccb);
  }
  const blocos = declaradas
    .sort((a, b) => a.ccb.ordem - b.ccb.ordem)
    .map(
      ({ contrato, ccb }): BlocoDaCcb => ({
        chave: ccb.id,
        ccb,
        unidades: contrato.unidades,
        vinculadas: contrato.apoios.map((unidade) =>
          vinculada(unidade, origem.get(unidade.documento_id) ?? null),
        ),
      }),
    );
  return [
    ...blocos,
    ...semNumero.map((chave): BlocoDaCcb => ({ chave, ccb: null, unidades: [], vinculadas: [] })),
  ];
}

export function rotuloDaCcb(bloco: BlocoDaCcb, indice: number): string {
  return bloco.ccb !== null
    ? t.rotulo.replace('{numero}', bloco.ccb.numero)
    : t.rotuloSemNumero.replace('{n}', String(indice + 1));
}

/** As outras CCBs que usam cada unidade de origem do bloco. */
export function compartilhadaCom(
  blocos: readonly BlocoDaCcb[],
  bloco: BlocoDaCcb,
): Map<string, CcbDoContratoData[]> {
  const usos = new Map<string, CcbDoContratoData[]>();
  for (const outro of blocos) {
    if (outro.ccb === null || outro.chave === bloco.chave) continue;
    for (const v of outro.vinculadas) {
      if (v.origem?.id !== bloco.chave) continue;
      usos.set(v.unidade.documento_id, [...(usos.get(v.unidade.documento_id) ?? []), outro.ccb]);
    }
  }
  return usos;
}

/** O arquivo do envio que não entrou em bloco nenhum. */
export function semCcb(envio: EnvioDaAberturaData | null | undefined): UnidadeDoEnvioData[] {
  if (envio == null || !envio.contratos.some((c) => c.ccb != null)) return [];
  return envio.perguntas.map((p) => p.unidade);
}

export interface DoReciboDaCcb {
  /** Os envios com arquivo e sem conclusão. */
  abertos: string[];
  naoExaminados: NaoExaminadoNaTela[];
}

/** Do recibo, o que é do envio da CCB (`null`: dos envios sem CCB). */
export function doReciboDaCcb(
  recibo: ReciboDaCustodiaData | null | undefined,
  ccbId: string | null,
): DoReciboDaCcb {
  if (recibo == null) return { abertos: [], naoExaminados: [] };
  const deles = recibo.recebimentos.filter((r) => (r.ccb_da_abertura_id ?? null) === ccbId);
  return {
    abertos: recebimentosAbertos({ ...recibo, recebimentos: deles }),
    naoExaminados: deles.flatMap((r) =>
      r.nao_examinados.map((n) => ({ id: n.item_id, nome: n.nome, motivo: n.motivo })),
    ),
  };
}

/** O número digitado repetido em outro bloco (pela régua do servidor: letras e dígitos). */
export function normalizado(numero: string): string {
  return numero.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

/** O documento como a tela o identifica: os oito primeiros caracteres do id, em maiúsculas. */
export function identificadorCurto(documentoId: string): string {
  return documentoId.replace(/-/g, '').slice(0, 8).toUpperCase();
}

/** O nome do arquivo na tela; com outro documento de mesmo nome nos blocos, leva o identificador. */
export function nomeDoArquivo(blocos: readonly BlocoDaCcb[], unidade: UnidadeDoEnvioData): string {
  const homonimo = blocos.some((b) =>
    [...b.unidades, ...b.vinculadas.map((v) => v.unidade)].some(
      (u) => u.nome === unidade.nome && u.documento_id !== unidade.documento_id,
    ),
  );
  return homonimo
    ? t.homonimo
        .replace('{nome}', unidade.nome)
        .replace('{id}', identificadorCurto(unidade.documento_id))
    : unidade.nome;
}

export interface EstadoDaTela {
  blocos: readonly BlocoDaCcb[];
  /** Arquivo em envio ou com falha (e recibo por fechar), em qualquer bloco. */
  pendencias: number;
  /** Das pendências, as com falha: a linha e o recibo que não fechou. */
  falhas?: number;
  /** Os blocos com o número em edição: inválido, em gravação ou diferente do gravado. */
  numerosPendentes?: number;
  semCcb: number;
}

export function podeConferir(estado: EstadoDaTela): boolean {
  return (
    estado.blocos.length > 0 &&
    // E199.F3: o arquivo vinculado conta — a CCB cujo pacote chegou em outro bloco tem arquivo.
    estado.blocos.every(
      (b) => b.ccb !== null && (b.unidades.length > 0 || b.vinculadas.length > 0),
    ) &&
    estado.pendencias === 0 &&
    (estado.numerosPendentes ?? 0) === 0 &&
    estado.semCcb === 0
  );
}

/** A linha de estado acima dos blocos: o que segura o "Conferir", na ordem do molde. */
export function estadoDaTela(estado: EstadoDaTela): string {
  const falhas = estado.falhas ?? 0;
  if (falhas > 0) return t.comFalha.replace('{n}', String(falhas));
  if (estado.pendencias > 0) {
    return t.aguardando.replace('{n}', String(estado.pendencias));
  }
  if ((estado.numerosPendentes ?? 0) > 0) return t.numeroPendente;
  if (estado.semCcb > 0) return t.comArquivoSemCcb;
  if (estado.blocos.some((b) => b.ccb === null)) return t.faltaNumero;
  if (estado.blocos.some((b) => b.unidades.length === 0 && b.vinculadas.length === 0)) {
    return t.faltaArquivo;
  }
  return '';
}
